export const getAsBoolean = (env: string | undefined): boolean => {
  // treated as false when env variable is not provided
  return JSON.parse(env || 'false');
};

export const parseJsonArray = (value: string): string[] => {
  return JSON.parse(value);
};

// Configuration type
interface Config {
  SEGMENT_ANALYTICS_KEY: string;

  SENTRY_DSN: string;
  SENTRY_ENV: string;

  BASKET_API_ENDPOINT: string;
  BASKET_API_BASE_URL: string;

  LOCATION_API_BASE_URL: string;
  USER_API_ENDPOINT: string;
  CHECKOUT_API_ENDPOINT: string;
  CHECKOUT_API_BASE_URL: string;
  BACKEND_API_ENDPOINT: string;
  CAMPAIGN_SERVICE_URL: string;

  CHECKOUT_APP_URL: string;
  MAKNET_WEB_BASE_URL: string;
  GOOGLE_MAP_API_KEY: string;
  STATIC_GOOGLE_MAP_API_KEY: string;

  PRODUCT_API_ENDPOINT: string;
  RECOMMENDATION_API_ENDPOINT: string;
  ENABLE_BASKET_DIVERSIFY: boolean;
  IS_COMPLEX_PROMOTION_ENABLED: string;
  COMPLEX_PROMOTION_MINIMUM_DATE: number;
  RECOMMENDATION_VARIANT: string;

  PRE_ORDER_START_DATE: string;
  PRE_ORDER_END_DATE: string;
  APP_ENV: string;
  IS_SECURE_BASKET_CHECKOUT_WITH_EVENT_ENABLED: boolean;
  IS_LOCATION_SVC_ENABLED: boolean;

  IS_ENABLE_LOCUS_LAT_LONG: boolean;
  IS_WEB_SYNC_BASKET_ENABLE: boolean;
  IS_APP_SYNC_BASKET_ENABLE: boolean;
  IS_MAPVIEW_GEOCODING_ENABLED: boolean;
  IS_ADDRESS_SECTIONS_ENABLED: boolean;

  IS_WEB_SYNC_ADDRESS_ENABLE: boolean;
  IS_APP_SYNC_ADDRESS_ENABLE: boolean;
  IS_GOOGLE_ZIP_ENABLED: boolean;
  IS_PIN_DISABLED_FOR_GOOGLE_MISMATCH: boolean;
  IS_EDIT_PIN_BUTTON_HIDDEN: boolean;
  IS_LOCATION_MISMATCH_NOTIFICATION_ENABLED: boolean;

  PIN_DISTANCE_THRESHOLD: number;
  ENABLE_LOCATION_SERVICE_ADDRESSES: boolean;

  DEFAULT_ZOOM_LEVEL: number;
  ZOOM_POWER: number;

  SHOPPING_CAROUSEL_MAX_ITEM_COUNT_VIEW: string;
  CAROUSEL_MFE_URL: string;
  ENABLED_BASKET_RECOMMENDATION_MFE: boolean;
  IS_RELOCATE_SHIPPING_ADDRESS_FIELDS_ENABLED: boolean;

  IS_EXPRESS_AFTER_CUTOFF_TIME_ENABLED: boolean;
  SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER: boolean;
  SUBDISTRICT_ALIAS_SETS: string[][];
  RECOM_SHOPPING_ENABLE_PRODUCT_CARD_V2: boolean;
  RECOM_LOYALTY_COMPLEX_PROMOTION_PRIORITY: boolean;
  RECOM_PLP_PROPOINT_PROMOTION_ENABLED: boolean;
  RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED: boolean;
  RECOM_LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED: boolean;
  RECOM_SHOPPING_PRODUCTLIST_PROPOINT_PROMOTION_ENABLE: boolean;
  RECOM_SHOPPING_MARKETING_BADGE_V2_ENABLED: boolean;
  RECOM_SHOPPING_WEIGHTED_FRESH_COMMUNICATE_IN_KG_ENABLED: boolean;
  RECOM_APP_ASSETS_URL: string;
  RECOM_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER: boolean;
  RECOM_LOYALTY_COMPLEX_PROMOTION_3P_ENABLED: boolean;

  HIDE_PRE_ORDER_DATES: boolean;
  ENABLE_UNIFY_BASKET: boolean;
  SHOW_PRO_POINT_INFO_FOR_3P: boolean;
  DISABLE_FORCE_SAVE_ON_ADDRESS_MISMATCH: boolean;
  DISABLE_ADDRESS_MUTATION: boolean;
  ENABLE_EXPRESS_OMNI_CHANNEL_VOUCHER: boolean;
}

const onlyPublicConfig = {
  BACKEND_API_ENDPOINT: process.env.REACT_APP_BACKEND_API_ENDPOINT || '',
  CHECKOUT_API_ENDPOINT: process.env.REACT_APP_CHECKOUT_API_ENDPOINT || '',
  CHECKOUT_API_BASE_URL: process.env.REACT_APP_CHECKOUT_API_BASE_URL || '',
  BASKET_API_ENDPOINT: process.env.REACT_APP_BASKET_API_ENDPOINT || '',
  BASKET_API_BASE_URL: process.env.REACT_APP_BASKET_API_BASE_URL || '',
  LOCATION_API_BASE_URL: process.env.REACT_APP_LOCATION_API_BASE_URL || '',
  USER_API_ENDPOINT:
    (getAsBoolean(process.env.REACT_APP_ENABLE_TENCENT_MIGRATION || 'false')
      ? process.env.REACT_APP_USER_API_TENCENT_ENDPOINT
      : process.env.REACT_APP_USER_API_ENDPOINT) || '',
  CHECKOUT_APP_URL: process.env.REACT_APP_CHECKOUT_APP_URL || '',
  MAKNET_WEB_BASE_URL: process.env.REACT_APP_MAKNET_WEB_BASE_URL || 'http://localhost:3000',
  CAMPAIGN_SERVICE_URL: process.env.REACT_APP_CAMPAIGN_SERVICE_URL || '',
  GOOGLE_MAP_API_KEY: process.env.REACT_APP_GOOGLE_JS_MAP_API_KEY || '',
  STATIC_GOOGLE_MAP_API_KEY: process.env.REACT_APP_GOOGLE_STATIC_MAP_API_KEY || '',
  COMPLEX_PROMOTION_MINIMUM_DATE: Number(process.env.REACT_APP_COMPLEX_PROMOTION_MINIMUM_DATE) || 7,
  IS_COMPLEX_PROMOTION_ENABLED: process.env.REACT_APP_IS_COMPLEX_PROMOTION_ENABLED || '1.64.0',
  PRE_ORDER_START_DATE: process.env.REACT_APP_PRE_ORDER_START_DATE || '',
  PRE_ORDER_END_DATE: process.env.REACT_APP_PRE_ORDER_END_DATE || '',
  APP_ENV: process.env.REACT_APP_ENV || '',
  IS_ENABLE_LOCUS_LAT_LONG: getAsBoolean(process.env.REACT_APP_ENABLE_LOCUS_LAT_LONG) || false,
  PRODUCT_API_ENDPOINT: process.env.REACT_APP_PRODUCT_API_ENDPOINT || '',
  RECOMMENDATION_API_ENDPOINT: process.env.REACT_APP_RECOMMENDATION_API_ENDPOINT || '',
  ENABLE_BASKET_DIVERSIFY: getAsBoolean(process.env.REACT_APP_ENABLE_BASKET_DIVERSIFY || 'false'),
  IS_SECURE_BASKET_CHECKOUT_ENABLED: getAsBoolean(process.env.REACT_APP_IS_SECURE_BASKET_CHECKOUT_ENABLED) || false,
  IS_SECURE_BASKET_CHECKOUT_WITH_EVENT_ENABLED:
    getAsBoolean(process.env.REACT_APP_IS_SECURE_BASKET_CHECKOUT_EVENT_ENABLED) || false,
  IS_LOCATION_SVC_ENABLED: getAsBoolean(process.env.REACT_APP_IS_LOCATION_SVC_ENABLED) || false,
  RECOMMENDATION_VARIANT: process.env.REACT_APP_RECOMMENDATION_VARIANT || 'RECOMMENDED_FOR_YOU',
  IS_WEB_SYNC_BASKET_ENABLE: getAsBoolean(process.env.REACT_APP_SYNC_BASKET_WEB) || false,
  IS_APP_SYNC_BASKET_ENABLE: getAsBoolean(process.env.REACT_APP_SYNC_BASKET_APP) || false,
  IS_MAPVIEW_GEOCODING_ENABLED: getAsBoolean(process.env.REACT_APP_IS_MAPVIEW_GEOCODING_ENABLED || 'false'),
  IS_ADDRESS_SECTIONS_ENABLED: getAsBoolean(process.env.REACT_APP_IS_ADDRESS_SECTIONS_ENABLED || 'false'),
  IS_APP_SYNC_ADDRESS_ENABLE: getAsBoolean(process.env.REACT_APP_SYNC_ADDRESS_APP) || false,
  IS_WEB_SYNC_ADDRESS_ENABLE: getAsBoolean(process.env.REACT_APP_SYNC_ADDRESS_WEB) || false,
  IS_GOOGLE_ZIP_ENABLED: getAsBoolean(process.env.REACT_APP_IS_GOOGLE_ZIP_ENABLED || 'false'),
  ENABLE_LOCATION_SERVICE_ADDRESSES: getAsBoolean(process.env.REACT_APP_ENABLE_LOCATION_SERVICE_ADDRESSES) || false,
  RECOM_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER:
    getAsBoolean(process.env.REACT_APP_RECOM_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER) || false,
  RECOM_SHOPPING_ENABLE_PRODUCT_CARD_V2:
    getAsBoolean(process.env.REACT_APP_RECOM_SHOPPING_ENABLE_PRODUCT_CARD_V2) || false,
  RECOM_LOYALTY_COMPLEX_PROMOTION_PRIORITY:
    getAsBoolean(process.env.REACT_APP_RECOM_LOYALTY_COMPLEX_PROMOTION_PRIORITY) || false,
  RECOM_PLP_PROPOINT_PROMOTION_ENABLED:
    getAsBoolean(process.env.REACT_APP_RECOM_PLP_PROPOINT_PROMOTION_ENABLED) || false,
  RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED:
    getAsBoolean(process.env.REACT_APP_RECOM_AS_PLP_PROPOINT_PROMOTION_ENABLED) || false,
  RECOM_LOYALTY_COMPLEX_PROMOTION_3P_ENABLED:
    getAsBoolean(process.env.REACT_APP_RECOM_LOYALTY_COMPLEX_PROMOTION_3P_ENABLED) || false,
  RECOM_LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED:
    getAsBoolean(process.env.REACT_APP_RECOM_LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED) || false,
  RECOM_SHOPPING_MARKETING_BADGE_V2_ENABLED:
    getAsBoolean(process.env.REACT_APP_RECOM_SHOPPING_MARKETING_BADGE_V2_ENABLED) || false,
  RECOM_SHOPPING_WEIGHTED_FRESH_COMMUNICATE_IN_KG_ENABLED:
    getAsBoolean(process.env.REACT_APP_RECOM_SHOPPING_WEIGHTED_FRESH_COMMUNICATE_IN_KG_ENABLED) || false,
  RECOM_APP_ASSETS_URL: process.env.REACT_APP_ASSETS_URL || '',
  RECOM_SHOPPING_PRODUCTLIST_PROPOINT_PROMOTION_ENABLE:
    getAsBoolean(process.env.REACT_APP_RECOM_SHOPPING_PRODUCTLIST_PROPOINT_PROMOTION_ENABLE) || false,
  IS_PIN_DISABLED_FOR_GOOGLE_MISMATCH: getAsBoolean(
    process.env.REACT_APP_DISABLE_EXPRESS_PIN_FOR_GOOGLE_MISMATCH || 'false'
  ),
  IS_EDIT_PIN_BUTTON_HIDDEN: getAsBoolean(process.env.REACT_APP_IS_EDIT_PIN_BUTTON_HIDDEN || 'false'),
  IS_LOCATION_MISMATCH_NOTIFICATION_ENABLED: getAsBoolean(
    process.env.REACT_APP_ENABLE_LOCATION_MISMATCH_NOTIFICATION || 'false'
  ),
  PIN_DISTANCE_THRESHOLD: Number(process.env.REACT_APP_PIN_DISTANCE_THRESHOLD || '5'),

  DEFAULT_ZOOM_LEVEL: Number(process.env.REACT_APP_DEFAULT_ZOOM_LEVEL || '18'),
  ZOOM_POWER: Number(process.env.REACT_APP_ZOOM_POWER || '0.5'),
  SHOPPING_CAROUSEL_MAX_ITEM_COUNT_VIEW: process.env.REACT_APP_SHOPPING_CAROUSEL_MAX_ITEM_COUNT_VIEW || '{}',

  CAROUSEL_MFE_URL: process.env.REACT_APP_CAROUSEL_MFE_URL || 'http://localhost:3001',
  ENABLED_BASKET_RECOMMENDATION_MFE: getAsBoolean(process.env.REACT_APP_ENABLED_BASKET_RECOMMENDATION_MFE) || false,
  IS_RELOCATE_SHIPPING_ADDRESS_FIELDS_ENABLED:
    getAsBoolean(process.env.REACT_APP_RELOCATE_SHIPPING_ADDRESS_FIELDS) || false,
  IS_EXPRESS_AFTER_CUTOFF_TIME_ENABLED:
    getAsBoolean(process.env.REACT_APP_IS_EXPRESS_AFTER_CUTOFF_TIME_ENABLED) || false,
  SUBDISTRICT_ALIAS_SETS: JSON.parse(process.env.REACT_APP_SUBDISTRICT_ALIAS_SETS || '[]'),
  SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER:
    getAsBoolean(process.env.REACT_APP_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER) || false,
  HIDE_PRE_ORDER_DATES: getAsBoolean(process.env.REACT_APP_HIDE_PRE_ORDER_DATES) || false,
  ENABLE_UNIFY_BASKET: getAsBoolean(process.env.REACT_APP_ENABLE_UNIFY_BASKET) || false,
  SHOW_PRO_POINT_INFO_FOR_3P: getAsBoolean(process.env.REACT_APP_ENABLE_PRO_POINT_INFO_FOR_3P) || false,
  DISABLE_FORCE_SAVE_ON_ADDRESS_MISMATCH:
    getAsBoolean(process.env.REACT_APP_DISABLE_FORCE_SAVE_ON_ADDRESS_MISMATCH) || false,
  DISABLE_ADDRESS_MUTATION: getAsBoolean(process.env.REACT_APP_DISABLE_ADDRESS_MUTATION) || false,
  ENABLE_EXPRESS_OMNI_CHANNEL_VOUCHER: getAsBoolean(process.env.REACT_APP_ENABLE_EXPRESS_OMNI_CHANNEL_VOUCHER) || false
};

const onlySecretConfig = {
  SEGMENT_ANALYTICS_KEY: process.env.REACT_APP_SEGMENT_ANALYTICS_KEY || '',
  SENTRY_DSN: process.env.REACT_APP_SENTRY_DSN || '',
  SENTRY_ENV: process.env.REACT_APP_SENTRY_ENV || ''
};

const config: Config = {
  ...onlyPublicConfig,
  ...onlySecretConfig
};

export {onlyPublicConfig, config};
